import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { Circle, InfoWindow, Marker, Polyline } from '@react-google-maps/api';
import { getV1CompaniesCompanyIdJobsJobIdSiteUsages } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { makeGapOptions } from '~components/Maps/polylineOptions';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { getMovingSiteLocationHistoriesByOrderId } from '~hooks/useLocationHistory/useLocationHistory';
import { JobSiteUsage, type OrderSiteUsage } from '~hooks/useSiteUsages/models';
import {
  createLabeledWaypointSvgDataUrl,
  JOB_COLOR_PALETTE,
} from '~pages/Admin/Geo/mapIcons';

import {
  buildTimedPath,
  interpolatePositionAtTime,
  type Point,
  type TimedPath,
} from './catmullRomSpline';
import { type JobTimelineEntry } from './DistanceJobsTimeline';
import DistanceRouteMap, { MapLoader } from './DistanceRouteMap';
import { splitPingsBySegments } from './getCycleSegmentBoundaries';
import { buildOrderMarkers, getJobLetter } from './orderMarkers';
import { makeTruckMarkerIcon, MOVING_SITE_CIRCLE_OPTIONS } from './timelineTicks';

interface Props {
  orderId: string;
  companyId: string;
  siteUsages: OrderSiteUsage[];
  pings: LocationHistoryMinimal[];
  isLoading?: boolean;
}

const DistanceMapOrder = ({
  orderId,
  companyId,
  siteUsages,
  pings,
  isLoading: isParentLoading,
}: Props) => {
  const [activeMarkerId, setActiveMarkerId] = useState<string | null>(null);
  const [selectedJobUuid, setSelectedJobUuid] = useState<string | null>(null);
  const [indicatorTime, setIndicatorTime] = useState<number | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isJobsLoading, setIsJobsLoading] = useState(true);
  const isLoading = !!isParentLoading || isJobsLoading;
  const mapRef = useRef<google.maps.Map | null>(null);
  const { getJobsByOrder } = useJob();
  const getJobsByOrderRef = useRef(getJobsByOrder);
  getJobsByOrderRef.current = getJobsByOrder;

  useEffect(() => {
    const controller = new AbortController();
    setIsJobsLoading(true);
    getJobsByOrderRef
      .current(orderId, { signal: controller.signal })
      .then((result) => {
        if (!controller.signal.aborted) {
          setJobs(result ?? []);
        }
      })
      .catch((error) => {
        if (controller.signal.aborted) return;

        console.error('Failed to fetch jobs for order', error);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsJobsLoading(false);
      });
    return () => controller.abort();
  }, [orderId]);

  // Cache job site usages fetched per-job, keyed by job UUID.
  const jobSiteUsagesCacheRef = useRef(new Map<string, JobSiteUsage[]>());
  const [selectedJobSiteUsages, setSelectedJobSiteUsages] = useState<
    JobSiteUsage[] | null
  >(null);

  // Reset selection and cache when order changes.
  useEffect(() => {
    jobSiteUsagesCacheRef.current = new Map();
    setSelectedJobSiteUsages(null);
    setSelectedJobUuid(null);
  }, [orderId]);

  // Fetch + cache job site usages when a job is selected.
  useEffect(() => {
    if (!selectedJobUuid) {
      setSelectedJobSiteUsages(null);
      return;
    }

    const cached = jobSiteUsagesCacheRef.current.get(selectedJobUuid);
    if (cached) {
      setSelectedJobSiteUsages(cached);
      return;
    }

    setSelectedJobSiteUsages(null);
    const controller = new AbortController();
    getV1CompaniesCompanyIdJobsJobIdSiteUsages({
      path: { 'company-id': companyId, 'job-id': selectedJobUuid },
      signal: controller.signal,
    })
      .then((res) => {
        if (controller.signal.aborted) return;
        const data = (res.data.data ?? []).map(JobSiteUsage.parse);
        jobSiteUsagesCacheRef.current.set(selectedJobUuid, data);
        setSelectedJobSiteUsages(data);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;

        console.error('Failed to fetch job site usages', error);
      });
    return () => controller.abort();
  }, [selectedJobUuid, companyId]);

  const jobsWithData = useMemo(() => {
    const set = new Set<string>();
    for (const p of pings) {
      if (p.jobId) set.add(p.jobId);
    }
    return set;
  }, [pings]);

  // Moving site support — works in both multi-job and single-job modes.
  // The order-level site_usages have the moving site info; we use the
  // first job with pings to fetch the moving site's location history.
  const [movingSitePings, setMovingSitePings] = useState<LocationHistoryMinimal[]>([]);

  const movingSiteInfo = useMemo(() => {
    for (const su of siteUsages) {
      const movingSite = su.site.movingSite;
      if (!movingSite?.id) continue;
      const primary = su.site.nextBillionGeofence;
      const movingGeofence =
        (primary?.movingGeofence ? primary : null) ??
        su.additionalGeofences.find((g) => g.movingGeofence);
      return {
        siteId: su.site.id,
        movingSiteId: movingSite.id,
        radius: movingGeofence?.circleRadius ?? null,
      };
    }
    return null;
  }, [siteUsages]);

  useEffect(() => {
    if (!companyId || !movingSiteInfo) {
      setMovingSitePings([]);
      return;
    }
    const controller = new AbortController();
    getMovingSiteLocationHistoriesByOrderId(
      companyId,
      orderId,
      movingSiteInfo.movingSiteId,
      controller.signal,
    )
      .then((result) => {
        if (!controller.signal.aborted) setMovingSitePings(result);
      })
      .catch(() => {});
    return () => controller.abort();
  }, [companyId, orderId, movingSiteInfo]);

  const movingSiteTimedPath = useMemo(
    () => buildTimedPath(movingSitePings),
    [movingSitePings],
  );

  const movingSitePosition = useMemo(() => {
    if (indicatorTime === null || !movingSiteInfo) return null;
    const { rawPath, pingTimes } = movingSiteTimedPath;
    if (rawPath.length === 0) return null;
    const clampedTime = Math.max(
      pingTimes[0],
      Math.min(indicatorTime, pingTimes[pingTimes.length - 1]),
    );
    const result = interpolatePositionAtTime(movingSiteTimedPath, clampedTime);
    if (!result) return null;
    return { position: result.position, radius: movingSiteInfo.radius };
  }, [indicatorTime, movingSiteTimedPath, movingSiteInfo]);

  const markers = useMemo(
    () =>
      buildOrderMarkers({
        selectedJobUuid,
        selectedJobSiteUsages,
        siteUsages,
        jobs,
        movingSiteInfo,
        movingSitePosition,
      }),
    [
      selectedJobSiteUsages,
      selectedJobUuid,
      jobs,
      siteUsages,
      movingSiteInfo,
      movingSitePosition,
    ],
  );

  const jobColorMap = useMemo(() => {
    const map = new Map<string, string>();
    jobs.forEach((job, index) => {
      map.set(job.id, JOB_COLOR_PALETTE[index % JOB_COLOR_PALETTE.length]);
    });
    return map;
  }, [jobs]);

  const jobTimelineEntries = useMemo(
    (): JobTimelineEntry[] =>
      jobs
        .filter((job) => jobsWithData.has(job.id))
        .map((job) => ({
          jobUuid: job.id,
          jobId: job.jobId,
          color: jobColorMap.get(job.id) ?? JOB_COLOR_PALETTE[0],
        })),
    [jobs, jobsWithData, jobColorMap],
  );

  // Group pings by job, then use cycle segment boundaries to split paths —
  // matching the same gaps shown in the timeline.
  const { solidByJob, gapByJob } = useMemo(() => {
    const jobPings = new Map<string, LocationHistoryMinimal[]>();

    for (const p of pings) {
      if (!p.jobId) continue;
      if (!jobPings.has(p.jobId)) jobPings.set(p.jobId, []);
      jobPings.get(p.jobId)!.push(p);
    }

    const solidResult = new Map<string, { lat: number; lng: number }[][]>();
    const gapResult = new Map<string, { lat: number; lng: number }[][]>();

    for (const [jobUuid, jp] of jobPings) {
      const { solidSegments, gapSegments } = splitPingsBySegments(jp);
      solidResult.set(jobUuid, solidSegments);
      gapResult.set(jobUuid, gapSegments);
    }

    return { solidByJob: solidResult, gapByJob: gapResult };
  }, [pings]);

  const visiblePings = useMemo(() => {
    if (!selectedJobUuid) return pings;
    return pings.filter((p) => p.jobId === selectedJobUuid);
  }, [pings, selectedJobUuid]);

  // precompute per-job path and timing data for truck interpolation
  const jobPathData = useMemo(() => {
    const result = new Map<string, TimedPath>();

    for (const p of pings) {
      if (!p.jobId) continue;
      if (!result.has(p.jobId)) {
        result.set(p.jobId, { rawPath: [], pingTimes: [] });
      }

      const lat = parseFloat(p.lat);
      const lng = parseFloat(p.lon);
      if (Math.abs(lat) <= 0.01 && Math.abs(lng) <= 0.01) continue;

      const data = result.get(p.jobId)!;
      data.rawPath.push({ lat, lng });
      data.pingTimes.push(p.occurredAt.valueOf());
    }

    return result;
  }, [pings]);

  // compute per-job truck positions at the current indicator time
  const jobTrucks = useMemo(() => {
    if (indicatorTime === null || selectedJobUuid) return [];

    const trucks: {
      jobUuid: string;
      position: Point;
      bearing: number;
      color: string;
    }[] = [];

    for (const [jobUuid, path] of jobPathData) {
      const result = interpolatePositionAtTime(path, indicatorTime);
      if (!result) continue;

      trucks.push({
        jobUuid,
        ...result,
        color: jobColorMap.get(jobUuid) ?? '#006F12',
      });
    }

    return trucks;
  }, [indicatorTime, selectedJobUuid, jobPathData, jobColorMap]);

  const handleJobClick = useCallback(
    (jobUuid: string) => {
      const next = selectedJobUuid === jobUuid ? null : jobUuid;
      setSelectedJobUuid(next);
      setIndicatorTime(null);

      if (!mapRef.current) return;

      const bounds = new window.google.maps.LatLngBounds();
      const relevantPings = next ? pings.filter((p) => p.jobId === next) : pings;

      for (const p of relevantPings) {
        const lat = parseFloat(p.lat);
        const lng = parseFloat(p.lon);
        if (Math.abs(lat) > 0.01 || Math.abs(lng) > 0.01) {
          bounds.extend({ lat, lng });
        }
      }

      if (!bounds.isEmpty()) {
        mapRef.current.fitBounds(bounds, 40);
      }
    },
    [selectedJobUuid, pings],
  );

  if (isLoading) {
    return <MapLoader />;
  }

  if (jobs.length === 0 && pings.length === 0) {
    return (
      <Typography
        sx={{
          color: 'text.secondary',
          py: 1,
        }}
      >
        {t('admin.geo.map_no_jobs')}
      </Typography>
    );
  }

  const activeMarker = markers.find((m) => m.id === activeMarkerId);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 1.5,
          flexWrap: 'wrap',
          px: 0.5,
        }}
      >
        {jobs
          .map((job, index) => ({ job, letter: getJobLetter(index) }))
          .filter(({ job }) => jobsWithData.has(job.id))
          .map(({ job, letter }) => {
            const color = jobColorMap.get(job.id) ?? JOB_COLOR_PALETTE[0];
            const isSelected = selectedJobUuid === job.id;

            return (
              <Box
                key={job.id}
                onClick={() => handleJobClick(job.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  borderRadius: 1,
                  px: 0.5,
                  py: 0.25,
                  outline: isSelected ? `2px solid ${color}` : '2px solid transparent',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    backgroundColor: color,
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {letter}
                </Box>
                <Typography variant="caption" noWrap>
                  {job.jobId}
                </Typography>
              </Box>
            );
          })}
      </Box>

      <DistanceRouteMap
        pings={visiblePings}
        hidePolyline
        hideTruckMarker={!selectedJobUuid}
        onTimeChange={setIndicatorTime}
        jobs={!selectedJobUuid ? jobTimelineEntries : undefined}
        mapRef={mapRef}
      >
        {Array.from(solidByJob.entries()).flatMap(([jobUuid, segments]) => {
          if (selectedJobUuid && jobUuid !== selectedJobUuid) return [];
          const color = jobColorMap.get(jobUuid) ?? '#FF8C00';
          const gapSegs = gapByJob.get(jobUuid) ?? [];
          const gapOpts = makeGapOptions(color);

          const polylines = segments
            .filter((seg) => seg.length > 1)
            .map((seg, i) => (
              <Polyline
                key={`${jobUuid}-seg-${i}`}
                path={seg}
                options={{
                  strokeColor: color,
                  strokeOpacity: 0.8,
                  strokeWeight: 3,
                }}
              />
            ));

          const gapPolylines = gapSegs
            .filter((seg) => seg.length > 1)
            .map((seg, i) => (
              <Polyline key={`${jobUuid}-gap-seg-${i}`} path={seg} options={gapOpts} />
            ));

          return [...polylines, ...gapPolylines];
        })}

        {markers.map((marker) => (
          <Marker
            key={marker.id}
            position={{ lat: marker.lat, lng: marker.lng }}
            opacity={selectedJobUuid && marker.jobUuid !== selectedJobUuid ? 0.3 : 1}
            icon={{
              url: createLabeledWaypointSvgDataUrl(
                marker.waypointType,
                marker.color,
                undefined,
                marker.isDefaultSite,
              ),
              anchor: new window.google.maps.Point(12, 12),
              scaledSize: new window.google.maps.Size(24, 24),
            }}
            onClick={() => setActiveMarkerId(marker.id)}
          />
        ))}

        {activeMarker && (
          <InfoWindow
            position={{ lat: activeMarker.lat, lng: activeMarker.lng }}
            onCloseClick={() => setActiveMarkerId(null)}
          >
            <Box sx={{ fontFamily: 'sans-serif', fontSize: '0.8125rem' }}>
              <strong>{activeMarker.siteName}</strong>
              <br />
              {`[${activeMarker.letter}] ${activeMarker.jobId}`}
            </Box>
          </InfoWindow>
        )}

        {jobTrucks.map((truck) => (
          <Marker
            key={`truck-${truck.jobUuid}`}
            position={truck.position}
            icon={makeTruckMarkerIcon(truck.color, truck.bearing)}
            zIndex={1000}
          />
        ))}

        {movingSitePosition && movingSitePosition.radius && (
          <Circle
            center={movingSitePosition.position}
            radius={movingSitePosition.radius}
            options={MOVING_SITE_CIRCLE_OPTIONS}
          />
        )}
      </DistanceRouteMap>
    </Box>
  );
};

export default DistanceMapOrder;
